<template>
  <footer class="mt-auto w-full p-3 md:p-4">
    <div
      class="w-full rounded-2xl overflow-hidden relative"
      :class="[
        footerBackground.className,
        footerText.text ? '' : 'text-brand-100',
      ]"
      :style="{
        ...footerBackground.style,
        ...(footerText.text ? { color: footerText.text } : {}),
      }"
    >
      <div
        v-if="footerBackground.showOverlay"
        class="absolute inset-0 bg-black/55 pointer-events-none"
        aria-hidden="true"
      />
      <div class="relative max-w-7xl mx-auto px-4 py-12">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div class="flex items-center gap-2 mb-4">
              <SiteLogo
                :src="site.logoFooter"
                :alt="site.name"
                :height="site.logoFooterHeight"
              />
            </div>
            <p v-if="footer.aboutText" class="text-sm leading-relaxed whitespace-pre-line">
              {{ footer.aboutText }}
            </p>
            <div v-if="themeSwitchEnabled" class="mt-5">
              <p
                class="text-xs font-medium uppercase tracking-wide mb-2"
                :class="footerText.text ? 'opacity-80' : 'text-brand-200'"
              >
                Aspect
              </p>
              <ThemeSwitch />
            </div>
          </div>

          <div>
            <h3
              class="font-semibold mb-4"
              :class="footerText.heading ? '' : 'text-white'"
              :style="footerText.heading ? { color: footerText.heading } : undefined"
            >
              {{ footer.shopTitle }}
            </h3>
            <ul class="space-y-2 text-sm">
              <li v-for="link in shopLinks" :key="`${link.label}-${link.to}`">
                <NuxtLink :to="link.to" class="hover:opacity-80 transition-opacity">{{ link.label }}</NuxtLink>
              </li>
            </ul>
          </div>

          <div>
            <h3
              class="font-semibold mb-4"
              :class="footerText.heading ? '' : 'text-white'"
              :style="footerText.heading ? { color: footerText.heading } : undefined"
            >
              {{ footer.infoTitle }}
            </h3>
            <ul class="space-y-2 text-sm">
              <li v-for="link in infoLinks" :key="`${link.label}-${link.to}`">
                <NuxtLink :to="link.to" class="hover:opacity-80 transition-opacity">{{ link.label }}</NuxtLink>
              </li>
            </ul>
          </div>

          <div>
            <h3
              class="font-semibold mb-4"
              :class="footerText.heading ? '' : 'text-white'"
              :style="footerText.heading ? { color: footerText.heading } : undefined"
            >
              {{ footer.contactTitle }}
            </h3>
            <ul class="space-y-3 text-sm">
              <li v-if="contactPhone" class="flex items-center gap-2">
                <UIcon
                  name="i-heroicons-phone"
                  class="w-4 h-4"
                  :class="footerText.text ? 'opacity-80' : 'text-brand-200'"
                />
                <a :href="`tel:${contactPhone}`" class="hover:opacity-80 transition-opacity">{{ contactPhone }}</a>
              </li>
              <li v-if="contactEmail" class="flex items-center gap-2">
                <UIcon
                  name="i-heroicons-envelope"
                  class="w-4 h-4"
                  :class="footerText.text ? 'opacity-80' : 'text-brand-200'"
                />
                <a :href="`mailto:${contactEmail}`" class="hover:opacity-80 transition-opacity">{{ contactEmail }}</a>
              </li>
              <li
                v-for="(location, index) in contactLocations"
                :key="`footer-location-${index}-${location.title}`"
                class="flex items-start gap-2"
              >
                <UIcon
                  name="i-heroicons-map-pin"
                  class="w-4 h-4 mt-0.5 shrink-0"
                  :class="footerText.text ? 'opacity-80' : 'text-brand-200'"
                />
                <span class="whitespace-pre-line">
                  <span v-if="location.title" class="font-medium">{{ location.title }}</span>
                  <span v-if="location.title" class="block opacity-90">{{ location.address }}</span>
                  <template v-else>{{ location.address }}</template>
                </span>
              </li>
            </ul>
            <div v-if="footer.socialLinks.length" class="flex flex-wrap items-center gap-2 mt-4">
              <a
                v-for="link in footer.socialLinks"
                :key="link.key"
                :href="link.url"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex size-8 items-center justify-center rounded-full bg-black/25 hover:bg-black/40 transition-colors"
                :class="footerText.text ? '' : 'text-brand-100'"
                :aria-label="link.label"
              >
                <UIcon :name="link.icon" class="size-4" />
              </a>
            </div>
          </div>
        </div>

        <div class="border-t border-white/15 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
          <div class="flex flex-col items-center sm:items-start gap-1">
            <p>&copy; {{ new Date().getFullYear() }} {{ footer.copyright }}</p>
            <button
              type="button"
              class="transition-opacity underline-offset-2 hover:underline hover:opacity-80"
              :class="footerText.text ? 'opacity-75' : 'text-brand-300 hover:text-white'"
              @click="openCookiePreferences"
            >
              Setări cookie-uri
            </button>
          </div>
          <div
            v-if="showFooterBadges"
            class="flex flex-wrap items-center justify-center gap-4"
          >
            <a
              v-for="logo in paymentLogos"
              :key="logo.id"
              :href="logo.href"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center opacity-90 hover:opacity-100 transition-opacity"
              :class="logo.chip ? 'footer-pay-chip' : ''"
              :title="logo.title"
            >
              <TbiLogo v-if="logo.tbi" size="md" />
              <img
                v-else
                :src="logo.src"
                :alt="logo.alt"
                class="w-auto object-contain"
                :class="logo.wide ? 'h-8 max-w-[180px]' : 'h-6 max-w-[6.5rem]'"
                loading="lazy"
                decoding="async"
              >
            </a>
            <a
              v-if="footer.badges.enabled && footer.badges.anpcSalImageUrl"
              :href="footer.badges.anpcSalLinkUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="opacity-90 hover:opacity-100 transition-opacity"
            >
              <AppImage
                :src="assetUrl(footer.badges.anpcSalImageUrl)"
                alt="ANPC - Soluționarea alternativă a litigiilor"
                width="161"
                height="40"
                sizes="161px"
                class="h-10 w-auto max-w-[220px] object-contain"
                loading="lazy"
                fit="contain"
              />
            </a>
            <a
              v-if="footer.badges.enabled && footer.badges.anpcSolImageUrl"
              :href="footer.badges.anpcSolLinkUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="opacity-90 hover:opacity-100 transition-opacity"
            >
              <AppImage
                :src="assetUrl(footer.badges.anpcSolImageUrl)"
                alt="ANPC - Soluționarea online a litigiilor"
                width="161"
                height="40"
                sizes="161px"
                class="h-10 w-auto max-w-[220px] object-contain"
                loading="lazy"
                fit="contain"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { resolveAssetUrl } from '~/utils/footerBadges'

const shopPath = useShopPath()
const shopSlug = useShopSlug()
const config = useRuntimeConfig()
const { site, footer, contactPage, themeSwitchEnabled, footerBackground, footerText, paymentMethods } = useSiteSettings()
const { openPreferences: openCookiePreferences } = useCookieConsent()

const paymentLogos = computed(() => {
  const methods = paymentMethods.value
  const logos: Array<{
    id: string
    alt: string
    title: string
    href: string
    src?: string
    wide?: boolean
    chip?: boolean
    tbi?: boolean
  }> = []
  if (methods.netopia) {
    logos.push({
      id: 'netopia',
      alt: 'Netopia',
      title: 'Plăți cu cardul prin Netopia',
      href: 'https://netopia-payments.com/',
      src: '/images/footer/netopia-white.svg',
      wide: true,
    })
  }
  if (methods.stripe) {
    logos.push({
      id: 'stripe',
      alt: 'Stripe',
      title: 'Plăți cu cardul prin Stripe',
      href: 'https://stripe.com/',
      src: '/images/checkout/stripe.svg',
      chip: true,
    })
  }
  if (methods.euplatesc) {
    logos.push({
      id: 'euplatesc',
      alt: 'EuPlătesc',
      title: 'Plăți cu cardul prin EuPlătesc',
      href: 'https://www.euplatesc.ro/',
      src: '/images/checkout/euplatesc.svg',
      chip: true,
    })
  }
  if (methods.payu) {
    logos.push({
      id: 'payu',
      alt: 'PayU',
      title: 'Plăți cu cardul prin PayU',
      href: 'https://payu.ro/',
      src: '/images/checkout/payu.svg',
      chip: true,
    })
  }
  if (methods.tbi) {
    logos.push({
      id: 'tbi',
      alt: 'tbi bank',
      title: 'Acceptăm plata în rate prin tbi bank',
      href: 'https://tbibank.ro/',
      tbi: true,
      chip: true,
    })
  }
  return logos
})

const showFooterBadges = computed(() =>
  footer.value.badges.enabled && (
    paymentLogos.value.length > 0
    || Boolean(footer.value.badges.anpcSalImageUrl)
    || Boolean(footer.value.badges.anpcSolImageUrl)
  ),
)

const contactPhone = computed(() => contactPage.value.phone || site.value.phone)
const contactEmail = computed(() => contactPage.value.email || site.value.email)

const contactLocations = computed(() => {
  const locations = contactPage.value.locations
    .filter((item) => item.address?.trim())
    .map((item) => ({
      title: item.title?.trim() || '',
      address: item.address.trim(),
    }))

  if (locations.length) return locations

  const fallback = site.value.address?.trim()
  return fallback ? [{ title: '', address: fallback }] : []
})

const shopLinks = computed(() => {
  void shopSlug.value
  return footer.value.shopLinks.map((link) => ({
    label: link.label,
    to: shopPath(link.to),
  }))
})

const infoLinks = computed(() => {
  void shopSlug.value
  return footer.value.infoLinks.map((link) => ({
    label: link.label,
    to: shopPath(link.to),
  }))
})

function assetUrl(url: string) {
  return resolveAssetUrl(url, config.public.apiOrigin as string)
}
</script>

<style scoped>
.footer-pay-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  border-radius: 0.65rem;
  background: #fff;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
}
</style>
